import type { ColoringTemplate } from '@/contracts/content';
import { fillStyleFor, paintMedium, type BrushKind, type StrokeStyle } from './media';

/**
 * The drawing engine (spec §9).
 *
 * Design decisions and the requirements they satisfy:
 *
 * §9.1 Normalised art coordinates — the document is the template's own
 *      width/height. Screen size, zoom and devicePixelRatio only affect
 *      presentation; every stored coordinate is in art space.
 *
 * §9.1 Separate layers — background, editable region fills, an editable raster
 *      brush layer, and a PROTECTED outline layer. Erase touches only the
 *      brush layer, so the line art can never be rubbed away.
 *
 * §9.1 No recursive flood fill — regions are authored vector masks, so "Fill"
 *      is a point-in-path test plus a colour assignment. It is exact, instant,
 *      never leaks through antialiased outlines, and never blocks the thread.
 *
 * §9.2 Bounded history — an operation log with periodic raster snapshots of
 *      the brush layer, NOT an unbounded array of base64 canvases.
 *
 * §9.1 Media, not one pen — each stroke records WHICH medium made it and the
 *      seed its grain was generated from, so a replay after undo reproduces
 *      the child's marks exactly rather than re-rolling their texture.
 *
 * §9.1 Staying inside the lines — a stroke is clipped to whatever the child
 *      STARTED in: begin on the lion and the mark stays on the lion; begin on
 *      the background and the subject masks itself out. A small hand cannot
 *      hold a line, and this makes that not matter. The clip is recorded on
 *      the operation, so a replay clips identically; it is a property of the
 *      mark, not of the settings in force when you undo.
 */

export interface StrokePoint {
  x: number;
  y: number;
  /** 0..1, from pointer pressure when the device reports it. */
  p: number;
}

export type Operation =
  | { kind: 'fill'; regionId: string; color: string; previous: string | null }
  | {
      kind: 'stroke';
      points: StrokePoint[];
      color: string;
      size: number;
      erase: boolean;
      /** Which medium laid this stroke down. */
      brush: BrushKind;
      /** Seed for this stroke's grain — replay must reproduce it exactly. */
      seed: number;
      /**
       * Where this mark was allowed to go: a region id, `OUTSIDE` for the
       * background around the subject, or null for the whole page.
       */
      clip: string | null;
    };

/** Clip token meaning "the page, minus every authored region". */
export const OUTSIDE = '~outside';

export interface EngineSnapshot {
  templateId: string;
  templateRevision: number;
  width: number;
  height: number;
  regionColors: Record<string, string>;
  /** PNG data for the brush layer only. */
  strokeLayer: string | null;
  opCount: number;
}

/** How many operations between raster checkpoints of the brush layer. */
const SNAPSHOT_EVERY = 16;
/** Hard ceiling on undo depth — documented, not an infinite promise (§9.2). */
export const HISTORY_LIMIT = 60;

interface Checkpoint {
  index: number;
  regionColors: Record<string, string>;
  strokeBitmap: ImageData | null;
}

export class ArtEngine {
  readonly template: ColoringTemplate;
  readonly width: number;
  readonly height: number;

  /** Region id -> colour. The vector fill state. */
  regionColors: Record<string, string> = {};

  /** Offscreen raster layer holding freehand brush marks only. */
  private strokeCanvas: HTMLCanvasElement;
  private strokeCtx: CanvasRenderingContext2D;

  /**
   * Scratch copy of the brush layer used to preview the stroke in progress.
   *
   * The preview has to sit in the SAME layer as the committed mark — under the
   * protected line art — or the mark visibly jumps behind the outline the
   * instant the finger lifts. Drawing it onto a copy of the brush layer also
   * makes the eraser preview correct: it rubs out brush marks and cannot touch
   * the fills or the line art, exactly as the real operation will.
   */
  private previewCanvas: HTMLCanvasElement | null = null;

  private regionPaths = new Map<string, Path2D>();
  /** Lazily built page-minus-subject mask, reused for every background mark. */
  private outsidePath: Path2D | null = null;
  private outlinePath: Path2D;
  private backgroundOutlinePath: Path2D | null;
  private detailPath: Path2D | null;

  private ops: Operation[] = [];
  /** Index of the next op to be written; ops beyond it are redoable. */
  private cursor = 0;
  private checkpoints: Checkpoint[] = [];

  private dirty = true;
  onChange?: () => void;

  constructor(template: ColoringTemplate, initial?: Partial<EngineSnapshot>) {
    this.template = template;
    this.width = template.width;
    this.height = template.height;

    for (const r of template.regions) this.regionPaths.set(r.id, new Path2D(r.d));
    this.outlinePath = new Path2D(template.outline);
    this.backgroundOutlinePath = template.backgroundOutline
      ? new Path2D(template.backgroundOutline)
      : null;
    this.detailPath = template.detail ? new Path2D(template.detail) : null;

    this.strokeCanvas = document.createElement('canvas');
    this.strokeCanvas.width = this.width;
    this.strokeCanvas.height = this.height;
    const ctx = this.strokeCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('canvas-unavailable');
    this.strokeCtx = ctx;
    this.strokeCtx.lineCap = 'round';
    this.strokeCtx.lineJoin = 'round';

    if (initial?.regionColors) this.regionColors = { ...initial.regionColors };
    this.checkpoints = [{ index: 0, regionColors: { ...this.regionColors }, strokeBitmap: null }];
  }

  /* ----------------------------------------------------------------- *
   * Restore
   * ----------------------------------------------------------------- */

  /** Rehydrate a saved draft. Returns false if the brush layer failed to load. */
  async restore(snapshot: EngineSnapshot): Promise<boolean> {
    this.regionColors = { ...snapshot.regionColors };
    this.ops = [];
    this.cursor = 0;
    let ok = true;
    if (snapshot.strokeLayer) {
      ok = await this.loadStrokeLayer(snapshot.strokeLayer);
    } else {
      this.strokeCtx.clearRect(0, 0, this.width, this.height);
    }
    this.checkpoints = [
      { index: 0, regionColors: { ...this.regionColors }, strokeBitmap: this.readStroke() },
    ];
    this.dirty = true;
    this.onChange?.();
    return ok;
  }

  private loadStrokeLayer(dataUrl: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      // Distinguish a denied image load from a network failure (§9.3).
      img.onload = () => {
        this.strokeCtx.clearRect(0, 0, this.width, this.height);
        this.strokeCtx.drawImage(img, 0, 0, this.width, this.height);
        resolve(true);
      };
      img.onerror = () => resolve(false);
      img.src = dataUrl;
    });
  }

  /* ----------------------------------------------------------------- *
   * Region hit testing + fill
   * ----------------------------------------------------------------- */

  /** Which authored region contains this art-space point, if any. */
  regionAt(x: number, y: number, ctx: CanvasRenderingContext2D): string | null {
    // Reverse order so regions authored later (drawn on top) win.
    const regions = this.template.regions;
    for (let i = regions.length - 1; i >= 0; i--) {
      const path = this.regionPaths.get(regions[i].id);
      if (path && ctx.isPointInPath(path, x, y)) return regions[i].id;
    }
    return null;
  }

  /** The authored outline of a region — used by the fill animation. */
  pathFor(regionId: string): Path2D | null {
    return this.regionPaths.get(regionId) ?? null;
  }

  fill(regionId: string, color: string) {
    const previous = this.regionColors[regionId] ?? null;
    if (previous === color) return;
    this.pushOp({ kind: 'fill', regionId, color, previous });
    this.regionColors[regionId] = color;
    this.afterApply();
    this.dirty = true;
    this.onChange?.();
  }

  /* ----------------------------------------------------------------- *
   * Brush strokes
   * ----------------------------------------------------------------- */

  commitStroke(
    points: StrokePoint[],
    style: StrokeStyle,
    erase: boolean,
    clip: string | null = null,
  ) {
    if (points.length === 0) return;
    this.pushOp({
      kind: 'stroke',
      points,
      color: style.color,
      size: style.size,
      erase,
      brush: style.kind,
      seed: style.seed,
      clip,
    });
    this.paintStroke(this.strokeCtx, points, style, erase, 1, clip);
    this.afterApply();
    this.dirty = true;
    this.onChange?.();
  }

  /**
   * Paints a stroke onto a context in art coordinates.
   *
   * `progress` draws only the leading part of the stroke — used by the
   * lift-off animation, which re-draws a mark transiently without it ever
   * entering the document.
   */
  paintStroke(
    ctx: CanvasRenderingContext2D,
    points: StrokePoint[],
    style: StrokeStyle,
    erase: boolean,
    progress = 1,
    clip: string | null = null,
  ) {
    ctx.save();
    const mask = this.clipPath(clip);
    if (mask) ctx.clip(mask.path, mask.rule);
    // Erase removes only from the brush layer — the outline and the region
    // fills live on other layers and are untouched (§9.1).
    ctx.globalCompositeOperation = erase ? 'destination-out' : 'source-over';
    if (erase) {
      // The eraser is always a plain round rubber, whichever medium is
      // selected: a child rubbing something out expects it gone, not
      // artistically removed.
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#000';
      const pts = progress >= 1 ? points : points.slice(0, Math.max(1, Math.ceil(points.length * progress)));
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, style.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.lineWidth = style.size;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) {
          const a = pts[i - 1];
          const b = pts[i];
          ctx.quadraticCurveTo(a.x, a.y, (a.x + b.x) / 2, (a.y + b.y) / 2);
        }
        ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    paintMedium(ctx, points, style, progress);
    ctx.restore();
  }

  /* ----------------------------------------------------------------- *
   * History
   * ----------------------------------------------------------------- */

  private pushOp(op: Operation) {
    // Writing after an undo discards the redo tail.
    if (this.cursor < this.ops.length) {
      this.ops.length = this.cursor;
      this.checkpoints = this.checkpoints.filter((c) => c.index <= this.cursor);
    }
    this.ops.push(op);
    this.cursor = this.ops.length;
  }

  /**
   * Called once the operation has actually been applied, so a checkpoint
   * captures real post-operation state.
   */
  private afterApply() {
    if (this.cursor % SNAPSHOT_EVERY === 0) {
      this.checkpoints.push({
        index: this.cursor,
        regionColors: { ...this.regionColors },
        strokeBitmap: this.readStroke(),
      });
    }

    // Bounded history: once past the limit, fold the oldest operations into a
    // new base checkpoint so undo depth stays predictable and memory does not
    // grow across a long drawing session (spec §18.1 "bounded undo/history").
    if (this.ops.length > HISTORY_LIMIT) {
      const drop = this.ops.length - HISTORY_LIMIT;
      // Current visible state becomes the state we must preserve.
      const keptOps = this.ops.slice(drop);
      // Rebuild the state as it was *before* the kept operations.
      const base = this.checkpointAt(drop);
      this.regionColors = { ...base.regionColors };
      this.strokeCtx.clearRect(0, 0, this.width, this.height);
      if (base.strokeBitmap) this.strokeCtx.putImageData(base.strokeBitmap, 0, 0);
      for (let i = base.index; i < drop; i++) {
        const op = this.ops[i];
        if (op.kind === 'fill') this.regionColors[op.regionId] = op.color;
        else this.replayStroke(op);
      }
      const newBase: Checkpoint = {
        index: 0,
        regionColors: { ...this.regionColors },
        strokeBitmap: this.readStroke(),
      };
      // Now replay the kept operations back on top.
      for (const op of keptOps) {
        if (op.kind === 'fill') this.regionColors[op.regionId] = op.color;
        else this.replayStroke(op);
      }
      this.ops = keptOps;
      this.cursor = keptOps.length;
      this.checkpoints = [newBase];
    }
  }

  private readStroke(): ImageData | null {
    try {
      return this.strokeCtx.getImageData(0, 0, this.width, this.height);
    } catch {
      return null;
    }
  }

  private checkpointAt(index: number): Checkpoint {
    let best = this.checkpoints[0];
    for (const c of this.checkpoints) if (c.index <= index && c.index >= best.index) best = c;
    return best;
  }

  /** Rebuild state at `target` from the nearest checkpoint at or below it. */
  private replayTo(target: number) {
    const cp = this.checkpointAt(target);
    this.regionColors = { ...cp.regionColors };
    this.strokeCtx.clearRect(0, 0, this.width, this.height);
    if (cp.strokeBitmap) this.strokeCtx.putImageData(cp.strokeBitmap, 0, 0);
    for (let i = cp.index; i < target; i++) {
      const op = this.ops[i];
      if (!op) continue;
      if (op.kind === 'fill') this.regionColors[op.regionId] = op.color;
      else this.replayStroke(op);
    }
    this.dirty = true;
  }

  /** Re-applies a recorded stroke with its own medium, seed and clip. */
  private replayStroke(op: Extract<Operation, { kind: 'stroke' }>) {
    this.paintStroke(
      this.strokeCtx,
      op.points,
      { color: op.color, size: op.size, kind: op.brush, seed: op.seed },
      op.erase,
      1,
      op.clip ?? null,
    );
  }

  /**
   * The mask for a clip token.
   *
   * `OUTSIDE` is built as the page rectangle with every region path added and
   * filled even-odd, which subtracts the whole subject in one clip — cheaper
   * and more exact than trying to paint around it.
   */
  private clipPath(clip: string | null): { path: Path2D; rule: CanvasFillRule } | null {
    if (!clip) return null;
    if (clip === OUTSIDE) {
      if (!this.outsidePath) {
        const p = new Path2D();
        p.rect(0, 0, this.width, this.height);
        for (const path of this.regionPaths.values()) p.addPath(path);
        this.outsidePath = p;
      }
      return { path: this.outsidePath, rule: 'evenodd' };
    }
    const region = this.regionPaths.get(clip);
    return region ? { path: region, rule: 'nonzero' } : null;
  }

  get canUndo() {
    return this.cursor > 0;
  }
  get canRedo() {
    return this.cursor < this.ops.length;
  }

  undo() {
    if (!this.canUndo) return;
    this.cursor -= 1;
    this.replayTo(this.cursor);
    this.onChange?.();
  }

  redo() {
    if (!this.canRedo) return;
    this.cursor += 1;
    this.replayTo(this.cursor);
    this.onChange?.();
  }

  /** True once the child has made any mark at all. */
  get hasContent() {
    return this.ops.length > 0 || Object.keys(this.regionColors).length > 0;
  }

  get operationCount() {
    return this.cursor;
  }

  /* ----------------------------------------------------------------- *
   * Composite rendering
   * ----------------------------------------------------------------- */

  /**
   * Draws the finished picture in the correct layer order (§9.1):
   * background → region fills → brush marks → protected outline → detail.
   */
  composite(
    ctx: CanvasRenderingContext2D,
    opts: {
      background?: string | null;
      omitRegions?: string[];
      /** A stroke in progress, drawn in the brush layer but never recorded. */
      live?: {
        points: StrokePoint[];
        style: StrokeStyle;
        erase: boolean;
        clip: string | null;
      } | null;
    } = {},
  ) {
    const bg = opts.background === undefined ? '#FFFFFF' : opts.background;
    const omit = opts.omitRegions ? new Set(opts.omitRegions) : null;
    ctx.save();
    ctx.clearRect(0, 0, this.width, this.height);
    if (bg) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, this.width, this.height);
    }

    for (const region of this.template.regions) {
      if (omit?.has(region.id)) continue;
      const spec = this.regionColors[region.id];
      if (!spec) continue;
      const path = this.regionPaths.get(region.id);
      if (!path) continue;
      // A fill may carry a finish (glitter, waves…). Textured finishes resolve
      // to a cached tile pattern, so a decorated region costs no more to
      // repaint than a plain one.
      ctx.fillStyle = fillStyleFor(ctx, spec);
      ctx.fill(path, 'nonzero');
    }

    ctx.drawImage(opts.live ? this.brushLayerWith(opts.live) : this.strokeCanvas, 0, 0);

    // The outline is drawn twice: a soft light casing first, then the dark
    // line. Without the casing, a child who fills a region with the same near
    // black as the line art loses the drawing entirely — the picture becomes a
    // dark rectangle. The casing is invisible over light fills and keeps the
    // line readable over dark ones, so the artwork survives any colour choice.
    const lineWidth = Math.max(2, this.width / 220);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // Background line art is drawn only when there IS a background — a
    // transparent export is a cutout of the subject alone.
    const paths = this.backgroundOutlinePath && bg
      ? [this.backgroundOutlinePath, this.outlinePath]
      : [this.outlinePath];

    for (const path of paths) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.lineWidth = lineWidth * 2.4;
      ctx.stroke(path);
      ctx.strokeStyle = '#2C1C38';
      ctx.lineWidth = lineWidth;
      ctx.stroke(path);
    }

    if (this.detailPath) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.lineWidth = lineWidth * 1.8;
      ctx.stroke(this.detailPath);
      ctx.fillStyle = '#2C1C38';
      ctx.fill(this.detailPath);
    }

    ctx.restore();
    this.dirty = false;
  }

  /** The brush layer plus an uncommitted stroke, on a scratch canvas. */
  private brushLayerWith(live: {
    points: StrokePoint[];
    style: StrokeStyle;
    erase: boolean;
    clip: string | null;
  }): HTMLCanvasElement {
    if (!this.previewCanvas) {
      const c = document.createElement('canvas');
      c.width = this.width;
      c.height = this.height;
      this.previewCanvas = c;
    }
    const ctx = this.previewCanvas.getContext('2d');
    if (!ctx) return this.strokeCanvas;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.drawImage(this.strokeCanvas, 0, 0);
    this.paintStroke(ctx, live.points, live.style, live.erase, 1, live.clip);
    return this.previewCanvas;
  }

  get needsRepaint() {
    return this.dirty;
  }
  markClean() {
    this.dirty = false;
  }

  /* ----------------------------------------------------------------- *
   * Export / persistence
   * ----------------------------------------------------------------- */

  snapshot(): EngineSnapshot {
    let strokeLayer: string | null = null;
    try {
      // Only serialise the brush layer if it has anything on it.
      const data = this.readStroke();
      if (data && hasInk(data)) strokeLayer = this.strokeCanvas.toDataURL('image/png');
    } catch {
      strokeLayer = null;
    }
    return {
      templateId: this.template.id,
      templateRevision: this.template.revision,
      width: this.width,
      height: this.height,
      regionColors: { ...this.regionColors },
      strokeLayer,
      opCount: this.cursor,
    };
  }

  /** Flattened PNG for previews, editions, printing and exports. */
  async toBlob(scale = 1, background: string | null = '#FFFFFF'): Promise<Blob> {
    const out = document.createElement('canvas');
    out.width = Math.round(this.width * scale);
    out.height = Math.round(this.height * scale);
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('canvas-unavailable');
    ctx.scale(scale, scale);
    this.composite(ctx, { background });
    return new Promise((resolve, reject) => {
      out.toBlob((b) => (b ? resolve(b) : reject(new Error('encode-failed'))), 'image/png');
    });
  }

  toDataUrl(
    scale = 1,
    background: string | null = '#FFFFFF',
    omitRegions?: string[],
  ): string {
    const out = document.createElement('canvas');
    out.width = Math.round(this.width * scale);
    out.height = Math.round(this.height * scale);
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('canvas-unavailable');
    ctx.scale(scale, scale);
    this.composite(ctx, { background, omitRegions });
    return out.toDataURL('image/png');
  }
}

function hasInk(data: ImageData): boolean {
  const d = data.data;
  for (let i = 3; i < d.length; i += 4 * 17) if (d[i] !== 0) return true;
  return false;
}
